// apps/web/src/features/crm/leads/card/LeadCardHeaderMeta.tsx
//
// Row-2 right-hand meta of the card header (#2308, D-2): assignee · source ·
// age. Each segment is omitted when its value is null (an unassigned lead skips
// the assignee, a source-less lead skips the source), and the surviving
// segments are joined with a middot so no dangling separator appears. `age` is
// the createdAt relative age (a PAST timestamp — `formatRelative` is correct
// here, unlike the future resumeAt in the banner).
import { Fragment, type ReactNode } from 'react';
import { Avatar, Icon } from '../../../../ui/index.js';
import { formatRelative } from '../../../../i18n/formatters.js';

export interface LeadCardHeaderMetaProps {
  assigneeName: string | null;
  source: string | null;
  createdAt: string;
}

export function LeadCardHeaderMeta({ assigneeName, source, createdAt }: LeadCardHeaderMetaProps) {
  const segments: ReactNode[] = [];
  if (assigneeName) {
    segments.push(
      <span
        key="assignee"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--fg-2)' }}
      >
        <Avatar name={assigneeName} size={16} />
        {assigneeName}
      </span>,
    );
  }
  if (source) {
    segments.push(
      <span key="source" style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <Icon name="inbox" size={12} style={{ color: 'var(--fg-4)' }} />
        {source}
      </span>,
    );
  }
  segments.push(
    <span key="age" data-testid="lead-card-age">
      {formatRelative(new Date(createdAt).getTime())}
    </span>,
  );

  return (
    <div
      data-testid="lead-card-meta"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
        color: 'var(--fg-3)',
        whiteSpace: 'nowrap',
      }}
    >
      {segments.map((seg, i) => (
        <Fragment key={i}>
          {i > 0 && <span style={{ color: 'var(--fg-4)' }}>·</span>}
          {seg}
        </Fragment>
      ))}
    </div>
  );
}
